import { useEffect, useMemo, useRef, type CSSProperties } from "react";
import {
  chapterThumbnails,
  formatClock,
  pickText,
  resolveAssetUrl,
  useGuided,
  useTourState,
} from "@heroncendre/player";
import type { Guide } from "./useGuide.js";
import { useChaines } from "./useChaines.js";
import { HAUTEURS, CREME, SAUGE, contour, desactive, ellipse, plein, surtitre, titreItalien } from "./guideStyles.js";
import { IconeBoussole, IconeChevron, IconeListe, IconeRejouer, Losange } from "./icones.js";

/**
 * ============================================================================
 *  ChapterEnd.tsx — L'écran de fin de chapitre
 * ----------------------------------------------------------------------------
 *  Les bandes restent, la barre est pleine, et ce voile — fond sombre flou — porte
 *  les choix : chapitre suivant (principal), précédent, explorer une scène du
 *  chapitre (vignettes), revoir le chapitre, ou revenir à la visite libre. Rien ne
 *  s'enchaîne seul. Aux extrémités du guide, le bouton sans suite est désactivé
 *  (`disabled`, opacité .4).
 *
 *  Le focus initial est sur « Chapitre suivant » (ou, à défaut, sur ce qu'il y a
 *  de mieux à proposer : le précédent, puis la première vignette).
 *
 *  ── Les vignettes ──────────────────────────────────────────────────────────
 *
 *  Le `preview` d'une scène empile ses six faces, carrées, la face avant en haut :
 *  on n'en montre que le milieu de la première, au format 16:10 — jamais l'image
 *  entière, qui montrerait le ciel et le sol d'une scène à l'autre. Le nom se lit
 *  SOUS l'image, sur plusieurs lignes : posé dessus il se perd dans la photo.
 *  Sur ordinateur, aucune barre de défilement : les cartes se partagent la largeur.
 * ==========================================================================*/

export function ChapterEnd({ guide, etroit }: { guide: Guide; etroit: boolean }) {
  const { t, scenes, language } = useChaines();
  const { assetBaseUrl } = useGuided();
  const tour = useTourState((s) => s.tour);
  const selector = useTourState((s) => s.context.selector);
  const { chapitre, chapitres, indexChapitre } = guide;
  const suivant = chapitres[indexChapitre + 1];
  const precedent = chapitres[indexChapitre - 1];
  const bandes = etroit ? HAUTEURS.telephone : HAUTEURS.ordinateur;

  const miniatures = useMemo(
    () => (chapitre ? chapterThumbnails(tour, chapitre.sceneIds, selector) : []),
    [tour, chapitre, selector],
  );

  // Focus initial : « Chapitre suivant ».
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const cible =
      racine.current?.querySelector<HTMLElement>("[data-focus-initial]:not(:disabled)") ??
      racine.current?.querySelector<HTMLElement>("button:not(:disabled)");
    cible?.focus({ preventScroll: true });
  }, [indexChapitre]);

  const libelle = (cle: "nextChap" | "prevChap", titre: string | undefined) => (
    <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 0, flex: 1 }}>
      <span style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", fontWeight: 600, opacity: 0.85 }}>
        {t(cle)}
      </span>
      {titre !== undefined && <span style={{ ...titreItalien(20), color: "inherit", ...ellipse, maxWidth: "100%" }}>{titre}</span>}
    </span>
  );

  const boutonSuivant = (
    <button
      type="button"
      data-focus-initial
      disabled={!suivant}
      onClick={guide.actions.chapitreSuivant}
      style={{ ...plein, ...grosBouton, ...(suivant ? null : desactive) }}
    >
      {libelle("nextChap", suivant?.titre)}
      <IconeChevron sens="droite" />
    </button>
  );
  const boutonPrecedent = (
    <button
      type="button"
      disabled={!precedent}
      onClick={guide.actions.chapitrePrecedent}
      style={{ ...contour, ...grosBouton, ...(precedent ? null : desactive) }}
    >
      <IconeChevron sens="gauche" />
      {libelle("prevChap", precedent?.titre)}
    </button>
  );

  return (
    <div
      ref={racine}
      role="region"
      aria-label={t("chapterDone")}
      lang={language}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: `calc(${bandes.haut}px + env(safe-area-inset-top))`,
        bottom: `calc(${bandes.bas}px + env(safe-area-inset-bottom))`,
        background: "rgba(11,19,16,.7)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 10,
        pointerEvents: "auto",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Centré tant que ça tient ; dès que ça déborde, la colonne défile depuis le haut.
        justifyContent: "safe center",
        padding: etroit ? "20px 16px" : "24px 28px",
        gap: etroit ? 18 : 22,
        boxSizing: "border-box",
      }}
    >
      <header style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span style={{ ...surtitre, gap: 10 }}>
          <Losange couleur={SAUGE} />
          {t("finTitle", { n: indexChapitre + 1, total: chapitres.length })}
          <Losange couleur={SAUGE} />
        </span>
        <h2 style={{ ...titreItalien(etroit ? 32 : 44), margin: 0 }}>{chapitre?.titre}</h2>
        <span style={{ fontSize: 13, color: "#E3D9C8" }}>
          {scenes(chapitre?.segments.length ?? 0)} · {formatClock(chapitre?.duree ?? 0)}
        </span>
      </header>

      <div
        style={{
          display: "flex",
          flexDirection: etroit ? "column" : "row",
          gap: 12,
          width: "100%",
          maxWidth: 720,
        }}
      >
        {etroit ? (
          <>
            {boutonSuivant}
            {boutonPrecedent}
          </>
        ) : (
          <>
            {boutonPrecedent}
            {boutonSuivant}
          </>
        )}
      </div>

      {miniatures.length > 0 && (
        <section
          aria-label={t("explore")}
          style={{ width: "100%", maxWidth: etroit ? 760 : 960, display: "flex", flexDirection: "column", gap: 12 }}
        >
          <span style={{ ...surtitre, justifyContent: "center" }}>
            <Losange couleur={SAUGE} />
            {t("explore")}
          </span>
          {/* Ordinateur : aucune barre de défilement, les cartes se partagent la largeur
              (`flex: 1 1 0`, plafonnées) et rétrécissent avec l'écran ou le nombre de scènes.
              Téléphone : une rangée qui défile, cartes de taille fixe. */}
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 8,
              paddingBottom: 4,
              justifyContent: etroit ? "flex-start" : "center",
              ...(etroit ? { overflowX: "auto" } : { overflow: "hidden" }),
            }}
          >
            {miniatures.map(({ sceneId, previewKey }, i) => (
              <button
                key={sceneId}
                type="button"
                onClick={() => guide.actions.explorer(sceneId)}
                aria-label={t("goToScene", { n: i + 1, nom: guide.nomScene(sceneId) })}
                style={{ ...carte, ...(etroit ? { flex: "none", width: 132 } : { flex: "1 1 0", minWidth: 0, maxWidth: 140 }) }}
              >
                <span
                  aria-hidden
                  style={{
                    ...image,
                    ...(previewKey
                      ? { backgroundImage: `url("${resolveAssetUrl(assetBaseUrl, previewKey)}")` }
                      : null),
                  }}
                >
                  <span style={numeroVignette}>{i + 1}</span>
                </span>
                <span style={nomVignette}>{pickText(tour.scenes.find((s) => s.id === sceneId)?.title, language) ?? ""}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 28, marginTop: 4 }}>
        <button
          type="button"
          style={lien}
          aria-haspopup="dialog"
          onClick={(event) => guide.actions.ouvrirMenu("fin", event.currentTarget)}
        >
          <IconeListe taille={16} />
          {t("allChapters")}
        </button>
        <button type="button" style={lien} onClick={guide.actions.revoirChapitre}>
          <IconeRejouer taille={16} />
          {t("replay")}
        </button>
        <button type="button" style={lien} onClick={() => guide.actions.quitter("libre")}>
          <IconeBoussole taille={16} />
          {t("backFree")}
        </button>
      </div>
    </div>
  );
}

const grosBouton: CSSProperties = {
  flex: 1,
  minHeight: 56,
  justifyContent: "flex-start",
  textAlign: "left",
  minWidth: 0,
  padding: "6px 18px",
  gap: 14,
};

/** Carte d'une scène : bordure fine, image 16:10 au-dessus, nom dessous, sur plusieurs lignes. */
const carte: CSSProperties = {
  appearance: "none",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 6,
  textAlign: "left",
  cursor: "pointer",
  color: CREME,
  background: "rgba(246,238,227,.04)",
  border: "1px solid rgba(246,238,227,.22)",
  borderRadius: 2,
};

/**
 * Le `preview` empile six faces carrées, la face avant en haut. Largeur de la carte =
 * largeur d'une face (`100% auto` : la hauteur suit le rapport de l'image, six faces carrées
 * soit six fois la largeur — un `600%` se compterait sur la hauteur du cadre, pas sur sa
 * largeur, et écraserait l'image) ; le cadre 16:10 est calé au milieu de la première : (largeur − hauteur) / 2 = 18,75 % de la
 * largeur, soit 3,488 % de (hauteur du cadre − hauteur de l'image).
 */
const image: CSSProperties = {
  position: "relative",
  display: "block",
  width: "100%",
  aspectRatio: "16 / 10",
  background: "linear-gradient(135deg, #2b3340, #161a20)",
  backgroundSize: "100% auto",
  backgroundPosition: "0 3.488%",
  backgroundRepeat: "no-repeat",
  borderRadius: 1,
  overflow: "hidden",
};

const numeroVignette: CSSProperties = {
  position: "absolute",
  top: 5,
  left: 5,
  minWidth: 20,
  height: 20,
  borderRadius: 2,
  background: "rgba(16,26,23,.85)",
  color: CREME,
  fontSize: 11,
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const nomVignette: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.3,
  fontWeight: 500,
  color: CREME,
  textWrap: "balance",
  overflowWrap: "anywhere",
};

/** Actions tertiaires : capitales espacées et icône, sans cadre. */
const lien: CSSProperties = {
  appearance: "none",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "0 4px",
  background: "none",
  border: "none",
  color: CREME,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".12em",
  textTransform: "uppercase",
  cursor: "pointer",
};
